import React, { useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

export function AssignJobToContractorDialog({ open, onOpenChange, contractor, onDone }) {
  const { user } = useAuth();
  const canEditFinancials = user?.role === "admin";
  const [wos, setWos] = useState(null);
  const [sel, setSel] = useState(null);
  const [payout, setPayout] = useState("");
  const [sdate, setSdate] = useState("");
  const [onlyMatch, setOnlyMatch] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel(null); setPayout(""); setSdate("");
    api.get("/admin/assignable-work-orders").then(({ data }) => setWos(data)).catch(() => setWos([]));
  }, [open]);

  const trades = [contractor?.trade, ...(contractor?.trades || [])].filter(Boolean).map((t) => t.toLowerCase());
  const states = [contractor?.state, ...(contractor?.coverage_states || [])].filter(Boolean);
  const matches = (w) => {
    if (!onlyMatch) return true;
    const cat = (w.service_category || "").toLowerCase();
    const tradeOk = trades.length === 0 || trades.some((t) => cat.includes(t) || t.includes(cat));
    const st = w.property?.state;
    const stateOk = !st || states.length === 0 || states.includes(st);
    return tradeOk && stateOk;
  };
  const filtered = (wos || []).filter(matches);

  const assign = async () => {
    if (!sel) return toast.error("Select a work order");
    setBusy(true);
    try {
      const payload = { contractor_id: contractor.id };
      if (sdate) payload.scheduled_date = sdate;
      if (canEditFinancials && payout !== "") payload.contractor_payout = Number(payout);
      await api.put(`/work-orders/${sel}/assign`, payload);
      toast.success("Work order assigned");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="assign-job-modal">
        <DialogHeader>
          <DialogTitle className="font-head">Assign Work Order — {contractor?.first_name} {contractor?.last_name}</DialogTitle>
          <DialogDescription>Pick an unassigned work order to assign to this contractor.</DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Checkbox data-testid="assign-only-match" checked={onlyMatch} onCheckedChange={(v) => setOnlyMatch(!!v)} /> Only show jobs matching this contractor's trade & state
        </label>
        {!wos ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div> : (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="assign-job-list">
            {filtered.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center" data-testid="assign-no-jobs">No matching unassigned work orders.</p> :
              filtered.map((w) => (
                <label key={w.id} data-testid={`assign-job-${w.id}`}
                  className={`block rounded-lg border p-3 cursor-pointer ${sel === w.id ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
                  <div className="flex items-start gap-2">
                    <input type="radio" name="wo" className="mt-1" checked={sel === w.id} onChange={() => setSel(w.id)} data-testid={`assign-job-radio-${w.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-slate-900 text-sm">{w.wo_number}</span><span className="text-xs text-blue-600">{w.service_category}</span><span className="text-xs text-slate-400">{w.urgency}</span></div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3"><span>{w.property?.name || "—"}</span>{w.property?.state && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{w.property?.city}, {w.property?.state}</span>}</div>
                    </div>
                  </div>
                </label>
              ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {canEditFinancials && <div><Label>Contractor Payout</Label><Input data-testid="assign-job-payout" type="number" value={payout} onChange={(e) => setPayout(e.target.value)} className="mt-1.5" /></div>}
          <div><Label>Scheduled Date</Label><Input data-testid="assign-job-date" type="date" value={sdate} onChange={(e) => setSdate(e.target.value)} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="assign-job-submit" disabled={busy} onClick={assign} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
